import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  useTheme,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button
} from '@mui/material';
import {
  People,
  PersonAdd,
  SupervisedUserCircle,
  Security,
  CheckCircle,
  Warning,
  AdminPanelSettings,
  Group,
  ArrowForward,
  Event,
  CalendarMonth
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import useAdminStore from '@store/Admin/AdminStore';
import FeedWidget from '@/components/OrganizationFeed/FeedWidget';
import {
  DashboardHeader,
  StatTile,
  SmartAlertsWidget,
  QuickActionsBar,
  getAdminQuickActions,
  MiniCalendar,
  WallOfFameWidget
} from '@/components/Dashboard';
import {
  getCompanySettings,
  getAdminDashboardSummary,
  getMyAlerts,
  dismissAlert,
  getUpcomingHolidays,
  getUpcomingBirthdays,
  getRecentRecognitions,
  CompanySettings,
  Recognition,
  AdminDashboardSummary,
  Alert as AlertType,
  Holiday,
  Birthday
} from '@/services/DashboardAnalyticsService';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip
} from 'recharts';

const ROLE_COLORS: { [key: string]: string } = {
  'Admin': '#9C27B0',
  'HR': '#E91E63',
  'Program Manager': '#FF9800',
  'Employee': '#2196F3',
  'Assessment Creator': '#4CAF50'
};

const AdminDashboard: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const { dashboardStats, loading: storeLoading, fetchDashboardStats } = useAdminStore();

  // Local state for new features
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [adminSummary, setAdminSummary] = useState<AdminDashboardSummary | null>(null);
  const [alerts, setAlerts] = useState<AlertType[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [birthdays, setBirthdays] = useState<Birthday[]>([]);
  const [recognitions, setRecognitions] = useState<Recognition[]>([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  // Fetch new analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      setAnalyticsLoading(true);
      setAnalyticsError(null);

      try {
        const [settingsRes, summaryRes, alertsRes, holidaysRes, birthdaysRes, recognitionsRes] = await Promise.all([
          getCompanySettings(),
          getAdminDashboardSummary(),
          getMyAlerts(1, 5),
          getUpcomingHolidays(5),
          getUpcomingBirthdays(),
          getRecentRecognitions(4)
        ]);

        if (settingsRes.success && settingsRes.data) {
          setCompanySettings(settingsRes.data);
        }
        if (summaryRes.success && summaryRes.data) {
          setAdminSummary(summaryRes.data);
        }
        if (alertsRes.success && alertsRes.data) {
          setAlerts(alertsRes.data);
        }
        if (holidaysRes.success && holidaysRes.data) {
          setHolidays(holidaysRes.data);
        }
        if (birthdaysRes.success && birthdaysRes.data) {
          setBirthdays(birthdaysRes.data);
        }
        if (recognitionsRes.success && recognitionsRes.data) {
          setRecognitions(recognitionsRes.data);
        }
      } catch (error: any) {
        setAnalyticsError(error.message || 'Failed to load dashboard data');
      } finally {
        setAnalyticsLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  const handleRefresh = () => {
    fetchDashboardStats();
  };

  const handleDismissAlert = async (id: number) => {
    const result = await dismissAlert(id);
    if (result.success) {
      setAlerts(prev => prev.filter(a => a.id !== id));
    }
  };

  // Quick actions handlers
  const quickActionsHandlers = {
    onAddUser: () => navigate('/manage-users'),
    onManageRoles: () => navigate('/manage-users'),
    onViewLogs: () => navigate('/admin/logs'),
    onSystemSettings: () => navigate('/admin/settings')
  };

  const quickActions = getAdminQuickActions(quickActionsHandlers);

  // Prepare chart data
  const roleChartData = (adminSummary?.usersByRole || []).map(item => ({
    name: item.role,
    value: item.count,
    color: ROLE_COLORS[item.role] || '#9E9E9E'
  }));

  // Statistics
  const totalUsers = adminSummary?.totalUsers || dashboardStats?.totalUsers || 0;
  const totalProgramManagers = dashboardStats?.totalProgramManagers || 0;
  const totalHR = dashboardStats?.totalHR || 0;
  const systemHealth = adminSummary?.systemHealth || 'healthy';

  // Helper to format date string
  const formatHolidayDate = (dateStr: string): string => {
    const cleanDate = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const [year, month, day] = cleanDate.split('-').map(Number);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const date = new Date(year, month - 1, day, 12, 0, 0);
    return `${weekdays[date.getDay()]}, ${months[month - 1]} ${day}`;
  };

  // Calendar events from holidays and birthdays
  const calendarEvents = [
    ...holidays.map(h => ({
      date: h.date.includes('T') ? h.date.split('T')[0] : h.date,
      type: 'holiday' as const,
      title: h.name
    })),
    ...birthdays.map(b => ({
      date: b.birthday_this_year,
      type: 'birthday' as const,
      title: `${b.name}'s Birthday`
    }))
  ];

  if (storeLoading && analyticsLoading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress color="primary" />
          <Typography variant="h6" sx={{ ml: 2 }}>Loading dashboard...</Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{
        p: { xs: 2, sm: 3 },
        backgroundColor: theme.palette.background.default,
        borderRadius: '8px',
        margin: { xs: '8px', sm: '16px' },
        minHeight: 'calc(100vh - 64px)'
      }}>
        {/* Dashboard Header */}
        <DashboardHeader
          companyName={companySettings?.company_name || 'Nucleus'}
          companyLogo={companySettings?.company_logo_url}
          showRefresh
          onRefresh={handleRefresh}
          loading={analyticsLoading}
        />

        {/* Error Alert */}
        {analyticsError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setAnalyticsError(null)}>
            {analyticsError}
          </Alert>
        )}

        {/* Stats Tiles Row */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)'
          },
          gap: 2,
          mb: 4
        }}>
          <StatTile
            title="Total Users"
            value={totalUsers}
            icon={<People />}
            variant="primary"
            onClick={() => navigate('/manage-users')}
            tooltip="View all users"
          />
          <StatTile
            title="Program Managers"
            value={totalProgramManagers}
            icon={<SupervisedUserCircle />}
            variant="warning"
            onClick={() => navigate('/manage-users?role=PM')}
          />
          <StatTile
            title="HR Users"
            value={totalHR}
            icon={<PersonAdd />}
            variant="info"
            onClick={() => navigate('/manage-users?role=HR')}
          />
          <StatTile
            title="System Health"
            value={systemHealth === 'healthy' ? 'Healthy' : 'Warning'}
            icon={systemHealth === 'healthy' ? <CheckCircle /> : <Warning />}
            variant={systemHealth === 'healthy' ? 'success' : 'error'}
            subtitle="All systems operational"
          />
        </Box>

        {/* Main Content Grid */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 3,
          mb: 4
        }}>
          {/* User Distribution Chart */}
          <Card sx={{
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
            borderRadius: 2,
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Group sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    User Distribution by Role
                  </Typography>
                </Box>
                <Button
                  size="small"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/manage-users')}
                  sx={{ textTransform: 'none' }}
                >
                  Manage
                </Button>
              </Box>

              {roleChartData.length > 0 ? (
                <Box sx={{ height: 280 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={roleChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={(props: any) => `${(props.percent * 100).toFixed(0)}%`}
                      >
                        {roleChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: isDark ? '#333' : '#fff',
                          border: `1px solid ${isDark ? '#555' : '#ddd'}`,
                          borderRadius: 8
                        }}
                        formatter={(value) => [value ?? 0, 'Users']}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">No user data available</Typography>
                </Box>
              )}

              {/* Role Summary List */}
              <List sx={{ mt: 2 }}>
                {roleChartData.map((role, index) => (
                  <React.Fragment key={role.name}>
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: role.color
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={role.name}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                      />
                      <Chip
                        label={role.value}
                        size="small"
                        sx={{
                          backgroundColor: `${role.color}20`,
                          color: role.color,
                          fontWeight: 600
                        }}
                      />
                    </ListItem>
                    {index < roleChartData.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Smart Alerts */}
          <SmartAlertsWidget
            alerts={alerts}
            loading={analyticsLoading}
            unreadCount={alerts.filter(a => !a.is_read).length}
            onDismiss={handleDismissAlert}
            onViewAll={() => navigate('/admin/alerts')}
            onAlertClick={(alert) => alert.action_url && navigate(alert.action_url)}
          />
        </Box>

        {/* Second Row - System Info & Feed */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 3,
          mb: 4
        }}>
          {/* System Status Card */}
          <Card sx={{
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
            borderRadius: 2,
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Security sx={{ color: theme.palette.success.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  System Status
                </Typography>
              </Box>

              <List>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: theme.palette.success.main }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Database Connection"
                    secondary="Connected and operational"
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                  <Chip label="Online" size="small" color="success" />
                </ListItem>
                <Divider />
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: theme.palette.success.main }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Authentication Service"
                    secondary="All auth services running"
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                  <Chip label="Active" size="small" color="success" />
                </ListItem>
                <Divider />
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: theme.palette.success.main }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="API Services"
                    secondary="All endpoints responsive"
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                  <Chip label="Healthy" size="small" color="success" />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Quick Overview */}
          <Card sx={{
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
            borderRadius: 2,
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AdminPanelSettings sx={{ color: theme.palette.primary.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Quick Overview
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7, mb: 3 }}>
                As an Admin, you have the authority to manage HR and Program Manager users.
                Use the "Manage Users" section to add new users, edit existing ones, or remove users as needed.
              </Typography>

              {/* Recent Activity Stats */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 2,
                p: 2,
                borderRadius: 1,
                backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'
              }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {adminSummary?.recentRegistrations || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    New Users (7 days)
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {totalUsers}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Active Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Calendar & Holidays Row */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 3,
          mb: 4
        }}>
          {/* Mini Calendar */}
          <MiniCalendar
            events={calendarEvents}
            onDateClick={(date) => console.log('Clicked date:', date)}
          />

          {/* Upcoming Holidays */}
          <Card sx={{
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
            borderRadius: 2,
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Event sx={{ color: theme.palette.error.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Upcoming Holidays
                  </Typography>
                </Box>
                <Button
                  size="small"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/holidays')}
                  sx={{ textTransform: 'none' }}
                >
                  View All
                </Button>
              </Box>

              <List sx={{ p: 0 }}>
                {holidays.length > 0 ? holidays.slice(0, 4).map((holiday, index) => (
                  <React.Fragment key={holiday.id}>
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CalendarMonth sx={{ color: theme.palette.error.main }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={holiday.name}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondary={formatHolidayDate(holiday.date)}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <Chip
                        label={holiday.type}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          backgroundColor: holiday.type === 'public' ? '#F4433620' : '#FF980020',
                          color: holiday.type === 'public' ? '#F44336' : '#FF9800'
                        }}
                      />
                    </ListItem>
                    {index < Math.min(holidays.length, 4) - 1 && <Divider />}
                  </React.Fragment>
                )) : (
                  <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">No upcoming holidays</Typography>
                  </Box>
                )}
              </List>
            </CardContent>
          </Card>
        </Box>

        {/* Organization Feed Widget */}
        <Box sx={{ mb: 4 }}>
          <FeedWidget limit={3} />
        </Box>

        {/* Wall of Fame Widget */}
        <Box sx={{ mb: 4 }}>
          <WallOfFameWidget
            recognitions={recognitions}
            loading={analyticsLoading}
            canAddRecognition={false}
            onViewAll={() => navigate('/wall-of-fame')}
            maxItems={4}
          />
        </Box>

        {/* Quick Actions */}
        <QuickActionsBar actions={quickActions} variant="horizontal" />
      </Box>
    </Layout>
  );
};

export default AdminDashboard;
